import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Collapse,
  Grid,
  Modal,
  message,
  Typography,
  Space,
  List,
  Tooltip,
  Divider,
  Skeleton,
  Empty,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import { useAppSelector } from "../../../hooks/reduxHooks";
import {
  getTopicsApi,
  updateTopicApi,
  deleteTopicApi,
  createTopicApi,
} from "../../../services/topics.service";
import TopicDialog from "./TopicDialog";
import UpgradeDialog from "../../../components/common/UpgradeDialog";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../store/slices/profile.slice";

const { Title } = Typography;
const { Panel } = Collapse;

const ManageTopicsComponent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const { isPaidPlan } = useAppSelector((state) => state.currentPlan);

  useEffect(() => {
    setLoading(true);
    dispatch(
      getTopicsApi({
        params: {},
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (data) => {
          setTopics(data || []);
        },
        failureCallback: (error) => {
          message.error(
            "Failed to fetch topics: " + (error.message || "Unknown error")
          );
        },
      })
    );
  }, [dispatch]);

  const showModal = (type, topicId = null, subtopic = null) => {
    setModalType(type);
    setSelectedTopicId(topicId);
    setSelectedSubtopic(subtopic);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    setSelectedTopicId(null);
    setSelectedSubtopic(null);
  };

  const handleCreate = (values) => {
    const payload = {
      name: values.name,
      type: values.type,
      ...(values.type === "subtopic" && { parent_id: values.parent_id }),
    };
    dispatch(
      createTopicApi({
        params: payload,
        finalCallback: () => {},
        successCallback: () => {
          dispatch(
            getTopicsApi({
              params: {},
              finalCallback: () => {},
              successCallback: (data) => {
                setTopics(data || []);
                message.success(`New ${values.type} added`);
              },
              failureCallback: (error) => {
                message.error(
                  "Failed to refresh topics: " +
                    (error.message || "Unknown error")
                );
              },
            })
          );
          handleModalCancel();
        },
        failureCallback: (error) => {
          message.error(
            "Failed to add item: " + (error.message || "Unknown error")
          );
        },
      })
    );
  };

  const handleUpdate = (values) => {
    const payload = {
      id: values.type === "topic" ? selectedTopicId : selectedSubtopic.id,
      name: values.name,
      type: values.type,
      ...(values.type === "subtopic" && { parent_id: values.parent_id }),
    };
    dispatch(
      updateTopicApi({
        params: payload,
        finalCallback: () => {},
        successCallback: () => {
          dispatch(
            getTopicsApi({
              params: {},
              finalCallback: () => {},
              successCallback: (data) => {
                setTopics(data || []);
                message.success(
                  `${
                    values.type.charAt(0).toUpperCase() + values.type.slice(1)
                  } updated`
                );
              },
              failureCallback: (error) => {
                message.error(
                  "Failed to refresh topics: " +
                    (error.message || "Unknown error")
                );
              },
            })
          );
          handleModalCancel();
        },
        failureCallback: (error) => {
          message.error(
            "Failed to update item: " + (error.message || "Unknown error")
          );
        },
      })
    );
  };

  const handleDeleteTopic = (topicId) => {
    const topic = topics.find((t) => t.id === topicId);
    Modal.confirm({
      title: "Delete Topic",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${topic.name}"?`,
      onOk: () => {
        dispatch(
          deleteTopicApi({
            params: {
              topic_id: topicId,
              topic_type: topic.type,
            },
            finalCallback: () => {},
            successCallback: () => {
              setTopics(topics.filter((t) => t.id !== topicId));
              message.success("Topic deleted successfully");
            },
            failureCallback: (error) => {
              message.error(
                "Failed to delete topic: " + (error.message || "Unknown error")
              );
            },
          })
        );
      },
    });
  };

  const handleDeleteSubtopic = (topicId, subtopicId) => {
    const topic = topics.find((t) => t.id === topicId);
    const subtopic = topic.sub_topics.find((s) => s.id === subtopicId);
    Modal.confirm({
      title: "Delete Subtopic",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${subtopic.name}"?`,
      onOk: () => {
        dispatch(
          deleteTopicApi({
            params: {
              topic_id: subtopicId,
              topic_type: "subtopic",
            },
            finalCallback: () => {},
            successCallback: () => {
              setTopics(
                topics.map((t) =>
                  t.id === topicId
                    ? {
                        ...t,
                        sub_topics: t.sub_topics.filter(
                          (s) => s.id !== subtopicId
                        ),
                      }
                    : t
                )
              );
              message.success("Subtopic deleted successfully");
            },
            failureCallback: (error) => {
              message.error(
                "Failed to delete subtopic: " +
                  (error.message || "Unknown error")
              );
            },
          })
        );
      },
    });
  };

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 12 : 0,
          padding: "16px 0",
          paddingTop: "0",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Title level={4} style={{ marginBottom: 4, marginTop: 0 }}>
            Manage Topics
          </Title>
          <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
            Organize conversations by categories and subcategories
          </Typography.Text>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Button
            type="primary"
            icon={isPaidPlan ? <PlusOutlined /> : <LockOutlined />}
            onClick={() => {
              if (!isPaidPlan) {
                setShowUpgradeDialog(true);
              } else {
                showModal("add-topic");
              }
            }}
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            Add Topic
          </Button>
        </div>
      </div>
      {loading ? (
        <Skeleton
          active
          paragraph={{ rows: 6 }}
          title={false}
          style={{ marginTop: 16 }}
        />
      ) : (
        <>
          {topics.length > 0 ? (
            <div
              style={{
                padding: isMobile ? 0 : 4,
              }}
            >
              <Collapse
                accordion
                bordered={false}
                expandIconPosition="end"
                style={{ background: "transparent" }}
              >
              {topics.map((topic) => (
                <Panel
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        width: "100%",
                        flexWrap: isMobile ? "wrap" : "nowrap",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{topic.name}</div>
                      <Space size={4}>
                        <Tooltip title="Edit Topic">
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              showModal("edit-topic", topic.id);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Delete Topic">
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTopic(topic.id);
                            }}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  }
                  key={topic.id}
                  style={{
                    background: "#ffffff",
                    borderRadius: 10,
                    border: "1px solid #edf1f7",
                    marginBottom: 12,
                    overflow: "hidden",
                  }}
                >
                  <List
                    dataSource={topic.sub_topics || []}
                    split={false}
                    renderItem={(subtopic) => (
                      <List.Item
                        style={{
                          marginLeft: isMobile ? 0 : 32,
                          background: "#fbfcfe",
                          border: "1px solid #eef2f7",
                          borderRadius: 8,
                          padding: "10px 12px",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: isMobile ? "wrap" : "nowrap",
                        }}
                        actions={[
                          <Tooltip title="Edit Subtopic">
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              size="small"
                              onClick={() =>
                                showModal("edit-subtopic", topic.id, subtopic)
                              }
                            />
                          </Tooltip>,
                          <Tooltip title="Delete Subtopic">
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              danger
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSubtopic(topic.id, subtopic.id);
                              }}
                            />
                          </Tooltip>,
                        ]}
                      >
                        <span>{subtopic.name}</span>
                      </List.Item>
                    )}
                    footer={
                      <Button
                        type="dashed"
                        icon={isPaidPlan ? <PlusOutlined /> : <LockOutlined style={{ fontSize: 12 }} />}
                        onClick={() => {
                          if (!isPaidPlan) {
                            setShowUpgradeDialog(true);
                          } else {
                            showModal("add-subtopic", topic.id);
                          }
                        }}
                        block
                        style={{ marginTop: 4 }}
                      >
                        Add Subtopic
                      </Button>
                    }
                  />
                </Panel>
              ))}
              </Collapse>
            </div>
          ) : (
            <Empty
              description={
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary">
                    Topics help organize conversations by categories and subcategories.
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    Click <strong>"Add Topic"</strong> to get started.
                  </Typography.Text>
                </Space>
              }
            />
          )}
        </>
      )}
      <TopicDialog
        visible={isModalVisible}
        modalType={modalType}
        topics={topics}
        selectedTopicId={selectedTopicId}
        selectedSubtopic={selectedSubtopic}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onCancel={handleModalCancel}
      />
      <UpgradeDialog
        visible={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature="Adding topics"
      />
    </Card>
  );
};

export default ManageTopicsComponent;
